import { request } from "../api/client";
import type { Context, Page, RecordValue, Unit } from "./types";
import { loadStageSnapshot, stageWinners } from "./stageSnapshot";

export type HierarchyKind = "COMPETITION" | "STAGE" | "VOTING_ROUND" | "TEAM" | "MEMBER" | "MORE";
export type HierarchyNode = {
  key: string; id: string; kind: HierarchyKind; name: string; roundId?: string;
  parentKey?: string; children?: HierarchyNode[]; withdrawn?: boolean;
  publishedSnapshotId?: string; unitVersion?: number; winnerPartyIds?: string[];
  winner?: boolean; winnerUnavailable?: boolean;
};
export type HierarchySnapshot = { root: HierarchyNode; readAt: number; pages: Record<string, number>; scope: string; rootVersion: number };
const id = (value: unknown) => {
  if (typeof value !== "string" || !value) throw new Error("Invalid hierarchy response");
  return value;
};

/** Only expanded branches are fetched; each visible page remains server-authorized. */
export async function readCompetitionHierarchy(context: Context, expanded: Set<string>, pages: Record<string, number>, signal: AbortSignal, displayed?: HierarchySnapshot): Promise<HierarchySnapshot> {
  const unitId = context.unit!.unitId;
  const unit = await request<Unit>("/events/eventUnit?" + new URLSearchParams({ unitId, projection: "STRUCTURE" }), "GET", undefined, signal);
  if (unit.unitId !== unitId || unit.unitKind !== "COMPETITION" || unit.organizerId !== context.organizerId || unit.serviceProductId !== context.serviceProductId) throw new Error("Invalid hierarchy scope");
  const root: HierarchyNode = { key: `competition:${unitId}`, id: unitId, kind: "COMPETITION", name: unit.title };
  const scope = JSON.stringify([context.organizerId, context.serviceProductId, unitId]);
  const previous = new Map<string, HierarchyNode>();
  // Reuse only the currently displayed, scoped view during a local expand/collapse.
  // Polls, change notifications, explicit refresh and failed reads never pass it.
  if (displayed?.scope === scope && displayed.rootVersion === unit.version && Date.now() - displayed.readAt < 15_000) {
    const collect = (node: HierarchyNode) => { previous.set(node.key, node); node.children?.forEach(collect); };
    collect(displayed.root);
  }
  let calls = 1, nodes = 1;
  async function readVisibleWinners(parent: HierarchyNode, old?: HierarchyNode): Promise<void> {
    // Results are needed only when an expanded direct Round exposes team cards.
    // Merely browsing Season/Round names must not wait for result pagination.
    if (parent.kind !== "STAGE") return;
    parent.winnerPartyIds = undefined;
    parent.winnerUnavailable = undefined;
    if (!parent.children?.some(child => child.kind === "VOTING_ROUND" && expanded.has(child.key))) return;
    if (old?.children && old.publishedSnapshotId === parent.publishedSnapshotId &&
        (old.winnerPartyIds !== undefined || old.winnerUnavailable === true)) {
      parent.winnerPartyIds = old.winnerPartyIds;
      parent.winnerUnavailable = old.winnerUnavailable;
    } else if (parent.publishedSnapshotId) {
      // The published result belongs to this exact Season, never to a sibling
      // Season, a Round, the latest draft snapshot or a preliminary live rank.
      try {
        const snapshotId = parent.publishedSnapshotId;
        const result = await loadStageSnapshot(parent.id, snapshotId, async cursor => {
          if (++calls > 100) throw new Error("HIERARCHY_DISPLAY_LIMIT");
          const query = new URLSearchParams({ unitId: parent.id, snapshotId, limit: "100" });
          if (cursor) query.set("cursor", cursor);
          return request<RecordValue>("/events/eventResult?" + query, "GET", undefined, signal);
        }, signal);
        if (++calls > 100) throw new Error("HIERARCHY_DISPLAY_LIMIT");
        const current = await request<Unit>("/events/eventUnit?" + new URLSearchParams({ unitId: parent.id, projection: "STRUCTURE" }), "GET", undefined, signal);
        if (result.published !== true || current.unitId !== parent.id || current.unitKind !== "STAGE" ||
            current.parentUnitId !== unitId || current.organizerId !== context.organizerId ||
            current.serviceProductId !== context.serviceProductId || current.version !== parent.unitVersion ||
            current.publishedSnapshotId !== snapshotId) throw new Error("Invalid hierarchy result scope");
        const winners = stageWinners(result.items as RecordValue[]);
        if (winners.some(row => typeof row.partyId !== "string" || !row.partyId)) throw new Error("Invalid hierarchy winner");
        parent.winnerPartyIds = [...new Set(winners.map(row => String(row.partyId)))];
      } catch (error) {
        signal.throwIfAborted();
        // Results permission/provider failures must not remove ordinary
        // hierarchy access or leave a previously displayed winner in place.
        parent.winnerPartyIds = undefined;
        parent.winnerUnavailable = true;
        if (error instanceof Error && error.message === "HIERARCHY_DISPLAY_LIMIT") throw error;
      }
    }
  }
  async function visit(parent: HierarchyNode, season?: HierarchyNode): Promise<void> {
    if (!expanded.has(parent.key) || parent.kind === "MEMBER") return;
    const wanted = Math.max(1, Math.min(20, pages[parent.key] || 1));
    const old = previous.get(parent.key);
    if (parent.kind === "STAGE") season = parent;
    if (old?.children && wanted === Math.max(1, Math.min(20, displayed?.pages[parent.key] || 1))) {
      parent.children = old.children.map(child => ({ ...child, children: undefined }));
      if (parent.kind === "VOTING_ROUND") for (const child of parent.children)
        if (child.kind === "TEAM") child.winner = !child.withdrawn && season?.winnerPartyIds?.includes(child.id) === true;
      nodes += parent.children.length;
      if (nodes > 2000) throw new Error("HIERARCHY_DISPLAY_LIMIT");
      await readVisibleWinners(parent, old);
      for (const node of parent.children) await visit(node, season);
      return;
    }
    const path = parent.kind === "TEAM" ? "/events/partyGroup/members" : parent.kind === "VOTING_ROUND" ? "/events/eventEntries" : "/events/eventUnits";
    const params = new URLSearchParams({ limit: "100", ...(parent.kind === "TEAM" ? { partyId: parent.id, unitId: parent.roundId! } : parent.kind === "VOTING_ROUND" ? { roundId: parent.id } : { parentUnitId: parent.id }) });
    if (parent.kind === "COMPETITION" || parent.kind === "STAGE") params.set("projection", "STRUCTURE");
    const seen = new Set<string>(), cursors = new Set<string>();
    parent.children = [];
    let next: string | null | undefined;
    for (let page = 0; page < wanted; page++) {
      if (++calls > 100) throw new Error("HIERARCHY_DISPLAY_LIMIT");
      if (next) params.set("cursor", next);
      const result = await request<Page>(path + "?" + params, "GET", undefined, signal);
      if (!Array.isArray(result.items) || result.items.length > 100) throw new Error("Invalid hierarchy page");
      for (const row of result.items) {
        let node: HierarchyNode;
        if (parent.kind === "TEAM") {
          const person = id(row.individualId);
          node = { key: `${parent.key}/member:${person}`, id: person, kind: "MEMBER", name: String(row.name || "—") };
        } else if (parent.kind === "VOTING_ROUND") {
          const party = id(row.partyId), entry = id(row.entryId);
          node = { key: `${parent.key}/entry:${entry}`, id: party, kind: "TEAM", name: String(row.displayName || row.name || "—"), roundId: parent.id, withdrawn: row.status === "WITHDRAWN", winner: row.status !== "WITHDRAWN" && season?.winnerPartyIds?.includes(party) === true };
        } else {
          const child = row as RecordValue;
          const kind = parent.kind === "COMPETITION" ? "STAGE" : "VOTING_ROUND";
          if (child.unitKind !== kind || child.parentUnitId !== parent.id || child.organizerId !== context.organizerId || child.serviceProductId !== context.serviceProductId) throw new Error("Invalid hierarchy branch");
          const childId = id(child.unitId);
          node = { key: `${parent.key}/${kind}:${childId}`, id: childId, kind, name: String(child.title || "—"), unitVersion: Number(child.version), publishedSnapshotId: kind === "STAGE" && typeof child.publishedSnapshotId === "string" && child.publishedSnapshotId ? child.publishedSnapshotId : undefined };
        }
        if (!seen.has(node.key)) { seen.add(node.key); parent.children.push(node); if (++nodes > 2000) throw new Error("HIERARCHY_DISPLAY_LIMIT"); }
      }
      next = result.nextCursor;
      if (!next) break;
      if (cursors.has(next)) throw new Error("Invalid hierarchy cursor");
      cursors.add(next);
    }
    await readVisibleWinners(parent, old);
    for (const node of parent.children) await visit(node, season);
    if (next) parent.children.push({ key: `${parent.key}/more`, id: parent.id, parentKey: parent.key, kind: "MORE", name: "", children: undefined });
  }
  await visit(root);
  signal.throwIfAborted();
  return { root, readAt: previous.size ? displayed!.readAt : Date.now(), pages: { ...pages }, scope, rootVersion: unit.version };
}

export type HierarchySize = { width: number; height: number };
export type HierarchyPosition = HierarchySize & { node: HierarchyNode; x: number; y: number; depth: number };
export const hierarchyGeometry = { nodeWidth: 280, nodeHeight: 72, levelGap: 80, rowGap: 20, padding: 36, toggleOffset: 20 } as const;
export function hierarchyLayout(root: HierarchyNode, rtl: boolean, sizes: ReadonlyMap<string, HierarchySize> = new Map()) {
  const positions: HierarchyPosition[] = [], links: { from: HierarchyPosition; to: HierarchyPosition }[] = [];
  const { nodeWidth, nodeHeight, levelGap, rowGap, padding, toggleOffset } = hierarchyGeometry;
  const columns: number[] = [], offsets: number[] = [];
  const subtreeHeights = new Map<string, number>();
  const sizeOf = (node: HierarchyNode) => sizes.get(node.key) || { width: nodeWidth, height: nodeHeight };
  function measure(node: HierarchyNode, depth: number): number {
    columns[depth] = Math.max(columns[depth] || 0, sizeOf(node).width);
    const children = node.children || [];
    const height = Math.max(sizeOf(node).height, children.reduce((sum, child) => sum + measure(child, depth + 1), 0) + Math.max(0, children.length - 1) * rowGap);
    subtreeHeights.set(node.key, height); return height;
  }
  measure(root, 0);
  let nextX = padding;
  for (const width of columns) { offsets.push(nextX); nextX += width + levelGap; }
  function place(node: HierarchyNode, depth: number, top: number): HierarchyPosition {
    const height = subtreeHeights.get(node.key)!, childNodes = node.children || [];
    const childrenHeight = childNodes.reduce((sum, child) => sum + subtreeHeights.get(child.key)!, 0) + Math.max(0, childNodes.length - 1) * rowGap;
    let childTop = top + (height - childrenHeight) / 2;
    const children = childNodes.map(child => { const position = place(child, depth + 1, childTop); childTop += subtreeHeights.get(child.key)! + rowGap; return position; });
    const size = sizeOf(node);
    const center = top + height / 2;
    const position = { node, depth, x: offsets[depth], y: center - size.height / 2, ...size };
    positions.push(position); children.forEach(to => links.push({ from: position, to }));
    return position;
  }
  place(root, 0, padding);
  const width = nextX - levelGap + padding + toggleOffset + 12;
  const height = Math.max(180, Math.max(...positions.map(position => position.y + position.height)) + padding);
  if (rtl) positions.forEach(position => { position.x = width - position.x - position.width; });
  return { positions, links, width, height };
}
